import { CustomSlideType, TextAnswerSlideItemType } from '@llx/common';
import { PatientCaseContent } from '@llx/common';
import { isSlideItemSelectableAnswerItem } from '@llx/common';
import { SelectableAnswerSlideItemType } from '@llx/common';
import { isSlideItemTextAnswerItem } from '@llx/common';
import { QuestionSlideType } from '@llx/common';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ContextType,
  MeetingFieldsFragment,
  useCreateOrUpdateAnswerMutation,
} from '@/@generated/codegen/API';
import getSlideVotingStats from '@/features/meeting/answer/getSlideVotingStats';
import { UseAnswersBySlideReturnType } from '@/hooks/useAnswersBySlide';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { MeetingQuestionContext, TextChangeEvent } from './QuestionContext';
import { useAnswerGlowingLogic } from '../hooks/useAnswerGlowingLogic';
import { useAnswersSelectingLogic } from '../hooks/useAnswersSelectingLogic';

export type MeetingQuestionContextProps = {
  slide: QuestionSlideType | CustomSlideType;
  meeting: MeetingFieldsFragment | undefined;
  currentCase: PatientCaseContent;
  currentPatientCaseId: string | null;
  isUserHost: boolean;
  answersForSlideDetails: UseAnswersBySlideReturnType;

  children: React.ReactNode;
};

export default function MeetingQuestionContextProvider({
  children,
  slide,
  meeting,
  currentCase,
  isUserHost,
  answersForSlideDetails,
  currentPatientCaseId,
}: MeetingQuestionContextProps) {
  const voteItems: SelectableAnswerSlideItemType[] = useMemo(
    () => slide.items.filter(isSlideItemSelectableAnswerItem),
    [slide.items]
  );
  const textItems: TextAnswerSlideItemType[] = useMemo(
    () => slide.items.filter(isSlideItemTextAnswerItem),
    [slide.items]
  );

  const [saveTrigger, triggerSaveToDatabase] = useState<number>(0);
  const [isAnswerConfirmed, setAnswerConfirmed] = useState<boolean>(false);
  const [currentSelectedAnswers, setCurrentSelectedAnswers] = useState<string[]>([]);
  const [currentTextAnswers, setCurrentTextAnswers] = useState<string[]>(textItems.map(() => ''));
  const hostStats = useMemo(
    () => getSlideVotingStats(slide.items, answersForSlideDetails.answers),
    [slide.id, answersForSlideDetails.answers]
  );
  const [mutationCreateOrUpdateAnswer, mutationCreateOrUpdateAnswerStatus] =
    useCreateOrUpdateAnswerMutation();

  const [glowingVoteItem, setGlowingVoteItem] = useState<
    SelectableAnswerSlideItemType | undefined
  >();

  const slideHasAtLeastOneSelectableAnswer = voteItems.length > 0;
  //NOTE: we're checking that there is at least one answer to select and that the user has selected at least one answer
  const hasAtleastSelectedOneAnswer =
    slideHasAtLeastOneSelectableAnswer && currentSelectedAnswers.length > 0;

  const { onSelectableAnswerClick } = useAnswersSelectingLogic({
    slide,
    currentSelectedAnswers,
    setCurrentSelectedAnswers,
  });

  const saveToDatabase = async () => {
    if (mutationCreateOrUpdateAnswerStatus.loading) {
      captureSentry({
        title: 'saving to database some datas, but saving is still in progress!',
      });
      return; // security
    }

    if (!meeting || !currentCase || !currentPatientCaseId) {
      captureSentry({
        title: 'Can not save answer to database',
        detail: {
          meeting,
          currentCase,
          currentPatientCaseId,
        },
      });
      return;
    }
    console.log('this is what is being saved', currentTextAnswers);
    await mutationCreateOrUpdateAnswer({
      variables: {
        data: {
          contextType: ContextType.Meeting,
          objId: meeting.id,
          slideId: slide.id,
          patientCaseId: currentPatientCaseId,
          answerIndexes: {
            set: currentSelectedAnswers,
          },
          answerTexts: {
            set: currentTextAnswers,
          },
        },
      },
    });
  };

  const requestTextAnswerIndex = (answerID: string) => {
    const answerItem = textItems.find((item) => item.id === answerID);
    if (!answerItem) {
      captureSentry({
        title: 'QuestionContext: Answer item not found',
        detail: {
          answerID,
          textItems,
        },
      });
      throw new Error('QuestionContext: Answer item not found');
    }
    const itemIndex = textItems.indexOf(answerItem);
    return itemIndex;
  };

  const onTextAnswerChange = (events: TextChangeEvent[]) => {
    events.forEach((event) => {
      const itemIndex = requestTextAnswerIndex(event.answerID);
      setCurrentTextAnswers((prev) => {
        const newTextAnswers = [...prev];
        newTextAnswers[itemIndex] = event.text;
        return newTextAnswers;
      });
    });
    triggerSaveToDatabase(Date.now());
  };

  const onConfirmAnswer = () => {
    setAnswerConfirmed(true);
    triggerSaveToDatabase(Date.now());
  };

  const requestShouldBeDisabled = useCallback(() => {
    return false;
  }, []);

  useAnswerGlowingLogic({
    forceDeactivateGlowFeature: isUserHost,
    isAnswerConfirmed,
    hasAtleastSelectedOneAnswer,
    voteItems,
    glowingVoteItem,
    setGlowingVoteItem,
  });

  //when the user answer changes, we update the current selected answers
  // (in case the user reload for example, or on slide change)
  useEffect(() => {
    if (!answersForSlideDetails.userAnswers) {
      setCurrentSelectedAnswers([]);
      setAnswerConfirmed(false);
      setCurrentTextAnswers(textItems.map(() => ''));
      return;
    }
    const userAnswer = answersForSlideDetails.userAnswers[0];
    setCurrentSelectedAnswers(userAnswer.answerIndexes!);
    setAnswerConfirmed(userAnswer.answerIndexes!.length > 0);
    setCurrentTextAnswers(userAnswer.answerTexts!);
  }, [answersForSlideDetails.userAnswers]);

  useEffect(() => {
    if (saveTrigger === 0) return;
    console.log('user answer has been updated');
    void saveToDatabase();
  }, [saveTrigger]);

  return (
    <MeetingQuestionContext.Provider
      value={{
        contextType: ContextType.Meeting,
        currentSelectedAnswers,
        currentTextAnswers,
        isAnswerConfirmed,
        hostStats,
        onSelectableAnswerClick,
        onTextAnswerChange,
        onConfirmAnswer,
        glowingVoteItem,
        hasAtleastSelectedOneAnswer,
        slideHasAtLeastOneSelectableAnswer,
        requestTextAnswerIndex,
        isSaving: mutationCreateOrUpdateAnswerStatus.loading,
        requestShouldBeDisabled,
      }}
    >
      {children}
    </MeetingQuestionContext.Provider>
  );
}
